import type { DomainOffersEntry } from '@/pages/useCalculateOffers';
import { buildSkierCountOffersFetchKey } from '@/utils/offers-fetch-key';
import {
  buildAgeMapFromRestrictions,
  buildBirthDateMapFromRestrictions,
  buildPeopleFromSkierCounts,
} from '@/utils/calculate-offers-people';
import { totalPeople } from '@/utils/booking';
import { fetchDomainOffersEntries } from '@/utils/fetch-domain-offers-entries';
import { useBookSkiPassStore } from '@/store/book-ski-pass.store';
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Offer lines from trip skier counts only (no pass selections, no birthdates).
 * Used to keep Step 1 age categories visible on the ski pass step when birthdate-based
 * pricing returns a narrower set of lines (e.g. only Junior).
 * When `enabled` is false, returns [] and skips the network (primary already uses skier counts).
 */
export function useTripSkierCountsBaselineOffers(enabled: boolean): DomainOffersEntry[] {
  const selectedDomain = useBookSkiPassStore((state) => state.selectedDomain);
  const referenceResortDomains = useBookSkiPassStore((state) => state.referenceResortDomains);
  const stepTwoFormFieldsValues = useBookSkiPassStore((state) => state.stepTwoFormFieldsValues);

  const [dataByDomain, setDataByDomain] = useState<DomainOffersEntry[]>([]);
  const lastFetchKeyRef = useRef<string | null>(null);

  const fetchBaseline = useCallback(async () => {
    const domainIds = referenceResortDomains?.map((d) => d.id) ?? [];
    const selectedDomainId = selectedDomain?.pk;
    const shouldAddSelectedDomain =
      selectedDomainId != null && !domainIds.includes(selectedDomainId);
    const domainsToFetch =
      domainIds.length > 0
        ? shouldAddSelectedDomain
          ? [selectedDomainId, ...domainIds]
          : domainIds
        : selectedDomainId != null
          ? [selectedDomainId]
          : [];

    if (domainsToFetch.length === 0 || totalPeople(stepTwoFormFieldsValues.skierCounts) < 1) {
      setDataByDomain([]);
      lastFetchKeyRef.current = null;
      return;
    }

    const days = parseInt(stepTwoFormFieldsValues.daysOfSkiing, 10);
    const startDate = stepTwoFormFieldsValues.startingDate;
    if (!startDate || Number.isNaN(days) || days < 1) {
      setDataByDomain([]);
      return;
    }

    const ageMap = buildAgeMapFromRestrictions(selectedDomain?.restrictions);
    const birthDateMap = buildBirthDateMapFromRestrictions(
      selectedDomain?.restrictions,
      startDate
    );

    const people = buildPeopleFromSkierCounts(
      stepTwoFormFieldsValues.skierCounts,
      startDate,
      days,
      ageMap,
      birthDateMap
    );

    if (people.length === 0) {
      setDataByDomain([]);
      return;
    }

    const fetchKey = buildSkierCountOffersFetchKey(
      domainsToFetch,
      stepTwoFormFieldsValues,
      people
    );
    if (lastFetchKeyRef.current === fetchKey) return;
    lastFetchKeyRef.current = fetchKey;

    try {
      const results = await fetchDomainOffersEntries(
        domainsToFetch,
        people,
        selectedDomain ?? undefined
      );

      setDataByDomain(results);
    } catch {
      setDataByDomain([]);
      lastFetchKeyRef.current = null;
    }
  }, [selectedDomain, referenceResortDomains, stepTwoFormFieldsValues]);

  useEffect(() => {
    if (!enabled) {
      setDataByDomain([]);
      lastFetchKeyRef.current = null;
      return;
    }
    void fetchBaseline();
  }, [enabled, fetchBaseline]);

  return dataByDomain;
}
